import { useQuery } from '@tanstack/react-query';
import { Clock, ExternalLink, MapPin, Phone } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/lib/supabase';

interface Unidade {
  seq_unidade: number;
  nom_unidade: string;
  des_endereco: string | null;
  nom_cidade: string | null;
  cod_uf: string | null;
  num_telefone: string | null;
  des_horario_func: string | null;
  val_latitude: number;
  val_longitude: number;
}

export function MapaPage() {
  const unidades = useQuery({
    queryKey: ['cliente-unidades'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tab_unidades')
        .select(
          'seq_unidade, nom_unidade, des_endereco, nom_cidade, cod_uf, num_telefone, des_horario_func, val_latitude, val_longitude',
        )
        .eq('ind_ativo', true)
        .eq('cod_status', 'ATIVA');
      if (error) throw error;
      return (data ?? []) as Unidade[];
    },
  });

  function abrirMaps(u: Unidade) {
    const url = `https://www.google.com/maps/search/?api=1&query=${u.val_latitude},${u.val_longitude}`;
    window.open(url, '_blank');
  }

  return (
    <div className="space-y-5 p-4 pb-24">
      {/* Header de página */}
      <div className="octa-page-header octa-fade-in-up">
        <p className="text-overline text-muted-foreground uppercase">Onde lavar</p>
        <h1>Unidades</h1>
        {unidades.data?.length ? (
          <p>
            {unidades.data.length} {unidades.data.length === 1 ? 'unidade' : 'unidades'} disponível
            {unidades.data.length > 1 ? 'is' : ''} pra você
          </p>
        ) : null}
      </div>

      {unidades.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      ) : unidades.data?.length === 0 ? (
        <EmptyState
          variant="hero"
          icon={MapPin}
          title="Sem unidades cadastradas"
          description="Em breve unidades parceiras na sua cidade."
        />
      ) : (
        <div className="octa-stagger space-y-3">
          {unidades.data?.map((u) => (
            <Card key={u.seq_unidade} variant="elevated" className="overflow-hidden">
              <CardContent className="space-y-3 p-4">
                <div className="flex items-start gap-3">
                  <div className="bg-octa-gradient shadow-octa-sm flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-bold leading-tight">{u.nom_unidade}</p>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                      {u.des_endereco}
                      {u.nom_cidade && ` · ${u.nom_cidade}`}
                      {u.cod_uf && `/${u.cod_uf}`}
                    </p>
                  </div>
                </div>

                {(u.des_horario_func || u.num_telefone) && (
                  <div className="bg-muted/40 ring-border/40 space-y-1.5 rounded-xl p-3 text-xs ring-1">
                    {u.des_horario_func && (
                      <div className="flex items-center gap-2">
                        <Clock className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                        <span className="font-medium">{u.des_horario_func}</span>
                      </div>
                    )}
                    {u.num_telefone && (
                      <a
                        href={`tel:${u.num_telefone}`}
                        className="text-primary flex items-center gap-2 font-semibold hover:underline"
                      >
                        <Phone className="h-3.5 w-3.5 shrink-0" />
                        {u.num_telefone}
                      </a>
                    )}
                  </div>
                )}

                <button
                  onClick={() => abrirMaps(u)}
                  className="octa-tap text-primary border-primary/30 bg-primary/5 hover:bg-primary/10 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Abrir no Google Maps
                </button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
